import { pipe, prop, path, sortBy, toLower, sort, descend, ascend, curry, groupBy, test, reduce, filter, includes, concat, isEmpty } from 'ramda'

import { Ord, Ordering } from 'ramda/tools'


/**
 * Сортирует массив объектов по свойству `title` в порядке возрастания, игнорируя регистр.
 *
 * @template S - Тип объектов в массиве. Объекты должны иметь свойство `title` типа строка.
 * @param items - Массив объектов для сортировки.
 * @returns Новый массив, содержащий отсортированные объекты.
 */
export function sortByTitle<S extends { title: string }>(items: Array<S>) {
  return sortBy<S>(pipe(prop<string>('title'), toLower), items)
}


/**
 * Сортирует массив объектов по указанному свойству в порядке возрастания, игнорируя регистр.
 *
 * @template S - Тип объектов в массиве.
 * @template K - Имя свойства для сортировки. Должно быть ключом типа `S`.
 * @param propName - Имя свойства для сортировки.
 * @returns Функция, которая принимает массив объектов типа `S` и возвращает новый массив, отсортированный по указанному свойству.
 */
export function sortByProp<S, K extends keyof S = keyof S>(propName: K) {
  return sortBy<S>(pipe(prop<string>(propName), toLower)) as (source: Array<S>) => Array<S>
}


/**
 * Сортирует массив объектов по указанному пути к свойству в порядке возрастания, игнорируя регистр.
 *
 * @template S - Тип объектов в массиве.
 * @param arrayPath - Путь к свойству для сортировки, представленный массивом строк.
 * @returns Функция, которая принимает массив объектов типа `S` и возвращает новый массив, отсортированный по указанному пути к свойству.
 */
export function sortByPath<S>(arrayPath: Array<string>) {
  return sortBy<S>((item: S) => {
    const val = path(arrayPath, item)
    return typeof val === 'string' ? toLower(val) : ''
  })
}


type SortDirection = 'up' | 'down'

type SortFunction<T> = {
  (fn: (obj: T) => Ord, a: T, b: T): Ordering
  (fn: (obj: T) => Ord): (a: T, b: T) => Ordering
}

/**
 * Возвращает функцию сортировки в зависимости от указанного направления.
 *
 * @template A - Тип элементов массива.
 * @param dir - Направление сортировки: 'up' (по возрастанию) или 'down' (по убыванию).
 * @returns Функция сортировки, которая принимает функцию сравнения и массив для сортировки.
 */
export const direction: <T>(dir: SortDirection) => SortFunction<T> = dir => (
  dir === 'up' ? descend : ascend
)


type Getter<T> = (obj: T) => Ord

type SortCurry = {
  <A>(sortDirection: SortDirection, getProp: Getter<A>, items: Array<A>): Array<A>
  <A>(sortDirection: SortDirection): <B = A>(getProp: Getter<B>, items: Array<B>) => Array<B>
  <A>(sortDirection: SortDirection, getProp: Getter<A>): <B = A>(items: Array<B>) => Array<B>
  <A>(sortDirection: SortDirection): <B = A>(getProp: Getter<B>) => <C = B>(items: Array<C>) => Array<C>
}

/**
 * Каррированная функция сортировки для таблицы с поддержкой сортировки колонок с выбором направления.
 *
 * @template A - Тип элементов массива.
 * @param sortDirection - Направление сортировки: 'up' (по возрастанию) или 'down' (по убыванию).
 * @param getProp - Функция для извлечения значения для сортировки из произвольного объекта.
 * @param items - Массив для сортировки.
 * @returns Отсортированный массив.
 * @example
 * const items = [{ id: '10' }, { id: '11' }, { id: '9' }, { id: '12' }]
 * const result = tableColumnSort<{ id: string }>('down', prop('id'), items)
 *  // result: [{ id: '12' }, { id: '11' }, { id: '10' }, { id: '9' }]
 *
 * const res = tableColumnSort<{ id: string }>('down', prop('id'))(items)
 *  // result: [{ id: '12' }, { id: '11' }, { id: '10' }, { id: '9' }]
 *
 * const res = tableColumnSort<{ id: string }>('down')(prop('id'))(items)
 *  // result: [{ id: '12' }, { id: '11' }, { id: '10' }, { id: '9' }]
 */
export const tableColumnSort: SortCurry = curry(
  (sortDirection: SortDirection, getProp, items) => (
    sort(direction(sortDirection)(getProp), items)
  ),
)


/**
 * Каррированная функция для сортировки массива по указанному свойству и направлению.
 *
 * @template A - Тип элементов массива.
 * @param sortDirection - Направление сортировки: 'up' (по возрастанию) или 'down' (по убыванию).
 * @param getProp - Функция для извлечения значения для сортировки из произвольного объекта.
 * @param items - Массив для сортировки.
 * @returns Отсортированный массив.
 * @example
 * const items = [{ id: '10' }, { id: '11' }, { id: '9' }, { id: '12' }]
 * const result = sortDirectionWith<{ id: string }>('down', prop('id'), items)
 *  // result: [{ id: '12' }, { id: '11' }, { id: '10' }, { id: '9' }]
 *
 * const res = sortDirectionWith<{ id: string }>('down', prop('id'))(items)
 *  // result: [{ id: '12' }, { id: '11' }, { id: '10' }, { id: '9' }]
 *
 * const res = sortDirectionWith<{ id: string }>('down')(prop('id'))(items)
 *  // result: [{ id: '12' }, { id: '11' }, { id: '10' }, { id: '9' }]
 */
export const sortDirectionWith = tableColumnSort


/**
 * Каррированная функция для сортировки массива по указанному свойству и направлению
 * с поддержкой сортировки имен классов, учитывая номер класса.
 *
 * @template A - Тип элементов массива.
 * @param sortDirection - Направление сортировки: 'up' (по возрастанию) или 'down' (по убыванию).
 * @param getProp - Функция для извлечения значения для сортировки из произвольного объекта.
 * @param items - Массив для сортировки.
 * @returns Отсортированный массив.
 * @example
 * const items = [{ cName: '3б' }, { cName: '2б' }, { cName: '1б' }, { cName: '1а' }]
 * const result = tableColumnSortClass<{ cName: string }>('up', prop('cName'), items)
 *  // result: [{ cName: '1а' }, { cName: '1б' }, { cName: '2б' }, { cName: '3б' }]
 *
 * const result = tableColumnSortClass<{ cName: string }>('up', prop('cName'))(items)
 *  // result: [{ cName: '1а' }, { cName: '1б' }, { cName: '2б' }, { cName: '3б' }]
 */
export const tableColumnSortClass: SortCurry = curry((sortDirection, getProp, items) => sort(
  (val1, val2) => {
    const a = getProp(val1) // prop(sortKey, val1);
    const b = getProp(val2) // prop(sortKey, val2);
    const aa = parseInt(a, 10) || 0
    const bb = parseInt(b, 10) || 0
    return ((aa !== bb) // eslint-disable-line no-nested-ternary
      ? sortDirection === 'down' ? aa - bb : bb - aa
      : sortDirection === 'down' // eslint-disable-line no-nested-ternary
        ? (a < b ? -1 : a > b ? 1 : 0) // eslint-disable-line no-nested-ternary
        : (a > b ? -1 : a < b ? 1 : 0)) // eslint-disable-line no-nested-ternary
  },
  items,
))


/**
 * Каррированная функция для сортировки массива по указанному свойству и направлению
 * с поддержкой сортировки имен классов, учитывая номер класса.
 *
 * @template A - Тип элементов массива.
 * @param sortDirection - Направление сортировки: 'up' (по возрастанию) или 'down' (по убыванию).
 * @param getProp - Функция для извлечения значения для сортировки из произвольного объекта.
 * @param items - Массив для сортировки.
 * @returns Отсортированный массив.
 * @example
 * const items = [{ cName: '3б' }, { cName: '2б' }, { cName: '1б' }, { cName: '1а' }]
 * const result = sortClasses<{ cName: string }>('up', prop('cName'), items)
 *  // result: [{ cName: '1а' }, { cName: '1б' }, { cName: '2б' }, { cName: '3б' }]
 *
 * const result = sortClasses<{ cName: string }>('up', prop('cName'))(items)
 *  // result: [{ cName: '1а' }, { cName: '1б' }, { cName: '2б' }, { cName: '3б' }]
 */
export const sortClasses = tableColumnSortClass


/**
 * Каррированная функция для сортировки массива по указанному свойству и направлению
 * со сравнением чисел или строк с предварительным парсингом в число.
 *
 * @template A - Тип элементов массива.
 * @param sortDirection - Направление сортировки: 'up' (по возрастанию) или 'down' (по убыванию).
 * @param getProp - Функция для извлечения значения для сортировки из произвольного объекта.
 * @param items - Массив для сортировки.
 * @returns Отсортированный массив.
 * @example
 * const items = [{ value: '3.324' }, { value: '2.324' }, { value: '1.324' }, { value: '1.324' }]
 * const result = sortStringsAsNumbers<{ value: string }>('up', prop('value'), items)
 *  // result: [{ value: '1.324' }, { value: '1.324' }, { value: '2.324' }, { value: '3.324' }]
 */
export const sortStringsAsNumbers: SortCurry = curry((sortDirection, getProp, items) => sort(
  (val1, val2) => {
    const a = parseFloat(getProp(val1)) || 0
    const b = parseFloat(getProp(val2)) || 0
    return sortDirection === 'down' ? a - b : b - a
  },
  items,
))


/**
 * Каррированная функция для сортировки массива по указанному свойству и направлению
 * с учетом строк, начинающихся на цифры.
 *
 * @template A - Тип элементов массива.
 * @param sortDirection - Направление сортировки: 'up' (по возрастанию) или 'down' (по убыванию).
 * @param getProp - Функция для извлечения значения для сортировки из произвольного объекта.
 * @param items - Массив для сортировки.
 * @returns Отсортированный массив.
 * @example
 * const items = [{ value: 'Подготовительный' }, { value: '11а' }, { value: '1а' }, { value: 'Ромашка' }]
 * const result = sortStringsAsNumbers<{ value: string }>('up', prop('value'), items)
 *  // result: [{ value: '1а' }, { value: '11а' }, { value: 'Подготовительный' }, { value: 'Ромашка' }]
 */
export const sortNumbersAndText: SortCurry = curry((sortDirection, getProp, items) => {
  const { numbers = [], strings = [] } = groupBy(
    item => (test(/^\d*[.|,]*\d$/, getProp(item)) ? 'numbers' : 'strings'),
  )(items)
  return [
    ...sortStringsAsNumbers(sortDirection, getProp, numbers),
    ...sort(direction(sortDirection)(getProp), strings),
  ]
})


type StringGetter<T> = (obj: T) => string

type SortByArrayCurry = {
  <A>(arrange: Array<string>, getProp: StringGetter<A>, items: Array<A>): Array<A>
  <A>(arrange: Array<string>): <B = A>(getProp: StringGetter<B>, items: Array<B>) => Array<B>
  <A>(arrange: Array<string>, getProp: StringGetter<A>): <B = A>(items: Array<B>) => Array<B>
  <A>(arrange: Array<string>): <B = A>(getProp: StringGetter<B>) => <C = B>(items: Array<C>) => Array<C>
}

/**
 * Каррированная функция для сортировки массива по указанному свойству в соответствии с заданным массивом.
 *
 * @template A - Тип элементов массива.
 * @param arrange - Массив значений (из items по полю, определяющему getProp) в соответствии с которым производится сортировка.
 * @param getProp - Функция для извлечения значения для сортировки из произвольного объекта.
 * @param items - Массив для сортировки.
 * @returns Отсортированный массив.
 * @example
 * const items = [{ value: 'Подготовительный' }, { value: '11а' }, { value: '1а' }, { value: 'Ромашка' }]
 * const result = arrangeByArray<{ value: string }>(['11а', '1а', 'Подготовительный', 'Ромашка'], prop('value'), items)
 *  // result: [{ value: '11а' }, { value: '1а' }, { value: 'Подготовительный' }, { value: 'Ромашка' }]
 */
export const arrangeByArray: SortByArrayCurry = curry(
  <A>(arrange: Array<string>, getProp: StringGetter<A>, items: Array<A>) => {
    if (isEmpty(items)) {
      return items
    }
    const groupped = groupBy(getProp, items)
    const arranged = reduce((acc, val) => {
      if (groupped[val] && groupped[val][0]) {
        acc.push(groupped[val][0])
      }
      return acc
    }, [] as typeof items, arrange)

    if (arrange.length < items.length) {
      const restUnarranged = filter(
        item => !includes(getProp(item), arrange),
        items,
      )
      return concat(arranged, restUnarranged)
    }
    return arranged
  },
)
